/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Table as TableComponent, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FilterIcon } from 'assets/icon-filter.svg';
import { ReactComponent as FilterAppliedIcon } from 'assets/icon-filter-applied.svg';
import { ReactComponent as ClientIcon } from 'assets/icon-consulting.svg';
import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';

import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ButtonIcon } from 'shared/components/Buttons';
import FilterDropdownInput from 'shared/components/FilterDropdownInput';
import FilterMultiSelect from 'shared/components/FilterMultiSelect';
import Pagination from 'shared/components/Pagination';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';
import MainSpinner from 'shared/components/MainSpinner';
import FieldType from 'shared/helpers/fieldType';
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons';
import classNames from 'classnames';

import useApi from 'hooks/useApi';
import {
  loadAllClients,
  paginateClients,
  setClearClientList,
  setNewCommunication,
} from 'redux/actions/clientActions';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';
import { colors } from 'shared/defaultTheme';

import { TableHeader, TableHeaderText, TableBodyText } from '../styles';

const PAGE_SIZE = 10;
const MANUALLY_SYNC = 2;

const Table = ({ clientStates, tagsList }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const clientState = useSelector((state) => state.clientState);
  const auth = useSelector((state) => state.auth);
  const theme = useSelector((state) => state.theme);
  const { syncContacts } = theme;
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [allClients, setAllClients] = useState([]);
  const [columnSearch, setColumnSearch] = useState({
    clientName: undefined,
    cif: undefined,
    contactMail: undefined,
    contactPhone: undefined,
    direction: undefined,
    state: undefined,
  });
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openManuallySync, setOpenManuallySync] = useState(false);
  const [deleteManuallySync, setDeleteManuallySync] = useState(false);
  const [selectedClientDelete, setSelectedClientDelete] = useState();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const { clients, currentClients, generalSearchText, sortConfig, clearList, selectedCustomField } =
    clientState;

  const [{ loading }, getClients] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${auth.cusNavId}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingDelete }, deleteClient] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  const onFirstPage = () => {
    dispatch(paginateClients({ page: 1, pageSize: PAGE_SIZE }));
    setCurrentPage(1);
  };

  const onLastPage = () => {
    dispatch(paginateClients({ page: lastPage, pageSize: PAGE_SIZE }));
    setCurrentPage(lastPage);
  };

  const onNextPage = () => {
    const page = currentPage + 1;
    if (page > lastPage) {
      return;
    }
    dispatch(paginateClients({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onPreviousPage = () => {
    const page = currentPage - 1;
    if (page <= 0) {
      return;
    }
    dispatch(paginateClients({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onGoToPage = (page) => {
    dispatch(paginateClients({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const getTagsCount = (clientTagsId) => {
    const tags = tagsList?.filter((tag) => clientTagsId?.includes(tag.id));
    return tags.length;
  };

  const getTagsNames = (clientTagsIdArray) => {
    const matchingTags = [];
    clientTagsIdArray?.forEach((tagId) => {
      const matchingTag = tagsList?.find((tag) => tag.id === tagId);
      if (matchingTag) {
        matchingTags.push({ text: matchingTag.text });
      }
    });
    return matchingTags;
  };

  useEffect(() => {
    const currentLastPage = Math.ceil(clients.length / PAGE_SIZE);
    onFirstPage();
    setLastPage(currentLastPage);
  }, [clients]);

  const getClientStateName = (clientStateId) => {
    const clientStateName = clientStates.find((el) => el.id === clientStateId);
    return clientStateName ? clientStateName?.text : null;
  };

  const getTagsOptions = () => {
    return tagsList.map((tag) => ({
      value: tag.id,
      name: tag.text,
    }));
  };

  const getOptionsFromAllClient = (fieldValue, fieldName) => {
    if (fieldValue === 'stateClient') {
      const options = [];
      const uniqueOptions = new Set();

      allClients.forEach((currentClient) => {
        const optionId = currentClient[fieldValue];
        const optionName = getClientStateName(optionId);

        if (optionId && optionId !== '' && !uniqueOptions.has(optionId)) {
          options.push({
            value: optionId,
            name: optionName,
          });

          uniqueOptions.add(optionId);
        }
      });

      return options;
    }

    return allClients.map((currentClient) => ({
      value: currentClient[fieldValue],
      name: currentClient[fieldName],
    }));
  };

  const createAndFilters = (
    typeIds,
    filterField,
    filterValue,
    isArray,
    isDate,
    generalSearchValue,
    optionFalse
  ) => {
    if (Array.isArray(filterValue) && filterValue.length === 0) {
      // eslint-disable-next-line no-param-reassign
      filterValue = [generalSearchValue];
    }

    return typeIds.map((typeId) => {
      const andFilter = [
        { model: 'Field', field: 'Id', Filter: [typeId] },
        {
          model: 'Field',
          field: filterField,
          Filter: isArray ? filterValue : [filterValue],
        },
      ];
      if (!isDate) {
        andFilter[1].isRegex = true;
      }
      if (optionFalse) {
        andFilter[1].isNegation = true;
      }
      return { andFilter };
    });
  };

  const getClientStateColor = (clientStateId) => {
    const clientStateColor = clientStates.find((el) => el.id === clientStateId);
    return clientStateColor ? clientStateColor?.color : null;
  };

  const searchClients = async (newColumnSearch) => {
    try {
      const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc !== 'none');
      const searchParam = sortOption
        ? {
            filters: [...sortConfig],
            reduce: {
              limit: 0,
              skip: 0,
            },
          }
        : {
            filters: [
              {
                model: 'Client',
                field: 'ClientId',
                asc: false,
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          };
      Object.keys(newColumnSearch).forEach((key) => {
        if (newColumnSearch[key]) {
          searchParam.filters.push(newColumnSearch[key]);
        }
      });

      const types = selectedCustomField.map((x) => {
        return x.type;
      });
      const hasOptionType = types.includes(FieldType.OPTION);
      if (selectedCustomField.length > 0 && hasOptionType) {
        const typeIds = selectedCustomField
          .filter((x) => {
            return x.type === FieldType.OPTION;
          })
          .map((x) => x.id);
        const isAnyTypeIdEndsWithCopy = typeIds.some((id) => id.endsWith('copy'));
        if (isAnyTypeIdEndsWithCopy) {
          const modifiedTypeIds = typeIds.map((id) => id.slice(0, -5));
          searchParam.filters.push({
            model: 'Field',
            orSubFilters: createAndFilters(
              modifiedTypeIds,
              'Value',
              'true',
              false,
              true,
              null,
              true
            ),
          });
        } else {
          searchParam.filters.push({
            model: 'Field',
            orSubFilters: createAndFilters(typeIds, 'Value', 'true', false, false),
          });
        }
      }
      if (generalSearchText && selectedCustomField.length > 0) {
        const hasListType = types.includes(FieldType.LIST);
        const hasMultiListType = types.includes(FieldType.MULTI_LIST);
        const hasCalendarType = types.includes(FieldType.CALENDAR);
        const hasOtherTypes = types.some((type) => {
          return (
            type === FieldType.TEXT ||
            type === FieldType.TEXT_AREA ||
            type === FieldType.NUMBER ||
            type === FieldType.CURRENCY
          );
        });
        const generalSearchTextLower = generalSearchText
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        if (hasListType || hasMultiListType) {
          const typeIds = selectedCustomField
            .filter((x) => {
              return x.type === FieldType.LIST || x.type === FieldType.MULTI_LIST;
            })
            .map((x) => x.id);
          const clientId = selectedCustomField
            .filter((item) => item.type === FieldType.LIST || FieldType.MULTI_LIST)
            .flatMap((item) =>
              item.members
                .filter((member) =>
                  member.i18List.some((x) =>
                    x.i18Name
                      .toLowerCase()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .includes(generalSearchTextLower)
                  )
                )
                .map((member) => member.id)
            );

          searchParam.filters.push({
            model: 'Field',
            orSubFilters: createAndFilters(
              typeIds,
              'Value',
              clientId,
              true,
              false,
              generalSearchText
            ),
          });
        }
        if (hasCalendarType) {
          const typeIds = selectedCustomField
            .filter((x) => {
              return x.type === FieldType.CALENDAR;
            })
            .map((x) => x.id);
          const convertDate = (dateString) => {
            const dateParts = dateString.split('/');
            const day = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10) - 1;
            const year = parseInt(dateParts[2], 10);
            const date = new Date(year, month, day);
            const options = {
              weekday: 'short',
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            };
            const formattedDate = date.toLocaleDateString('en-US', options).replace(/,/g, '');
            const formattedTime = '00:00:00';
            return `${formattedDate} ${formattedTime} GMT+0100 (hora est�ndar de Europa central)`;
          };
          searchParam.filters.push({
            model: 'Field',
            orSubFilters: createAndFilters(
              typeIds,
              'Value',
              convertDate(generalSearchText),
              false,
              true,
              generalSearchText
            ),
          });
        }
        if (hasOtherTypes) {
          const typeIds = selectedCustomField
            .filter((x) => {
              return (
                x.type === FieldType.TEXT ||
                x.type === FieldType.TEXT_AREA ||
                x.type === FieldType.NUMBER ||
                x.type === FieldType.CURRENCY
              );
            })
            .map((x) => x.id);
          searchParam.filters.push({
            model: 'Field',
            orSubFilters: createAndFilters(
              typeIds,
              'Value',
              `.*${generalSearchText}.*`,
              false,
              false
            ),
          });
        }
      }
      if (generalSearchText && selectedCustomField.length === 0) {
        searchParam.filterText = {
          regexFilter: generalSearchText,
        };
      }
      const {
        data: { errors, data },
      } = await getClients({
        data: searchParam,
      });

      if (errors.length) {
        return;
      }
      if (allClients.length === 0) {
        setAllClients(data);
      }
      setAllClients(data);

      dispatch(loadAllClients(data));
    } catch (error) {
      dispatch(loadAllClients([]));
    }
  };

  useEffect(() => {
    searchClients(columnSearch);
  }, [selectedCustomField]);

  useEffect(() => {
    if (generalSearchText != null) {
      searchClients(columnSearch);
    }
  }, [generalSearchText]);

  useEffect(() => {
    if (sortConfig.length > 0) {
      searchClients(columnSearch);
    }
  }, [sortConfig]);

  useEffect(() => {
    if (clearList) {
      setColumnSearch({
        clientName: undefined,
        cif: undefined,
        contactMail: undefined,
        contactPhone: undefined,
        direction: undefined,
        state: undefined,
      });
      dispatch(setClearClientList(false));
      searchClients();
    }
  }, [clearList]);

  const onSearchOptionOrText = async ({
    selectedOption,
    searchText,
    fieldSelectedOption,
    fieldSearchText,
    column,
  }) => {
    if (fieldSearchText === 'tagsClient') {
      const findTag = tagsList.find((el) =>
        el.text.toLowerCase().includes(searchText.toLowerCase())
      );
      searchText = findTag?.id ? findTag?.id : searchText;
    }
    if (fieldSearchText === 'stateClient') {
      const findState = clientStates.find((el) =>
        el.text.toLowerCase().includes(searchText.toLowerCase())
      );
      searchText = findState?.id ? findState?.id : searchText;
    }
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (selectedOption) {
      newColumnSearch[column] = {
        model: 'Client',
        field: fieldSelectedOption,
        filter: [selectedOption],
      };
    } else if (searchText) {
      newColumnSearch[column] = {
        model: 'Client',
        field: fieldSearchText,
        filter: [searchText],
        isRegex: true,
      };
    }
    searchClients(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const onSearchMultipleOptions = async ({ selectedOptions, fieldSearch, column }) => {
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (selectedOptions && selectedOptions.length > 0) {
      newColumnSearch[column] = {
        model: 'Client',
        field: fieldSearch,
        filter: selectedOptions,
      };
    }
    searchClients(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const showAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setOpenAlert(true);
  };

  const closeAlert = () => {
    setSelectedClientDelete(undefined);
    setAlertType('');
    setAlertMessage('');
    setOpenAlert(false);
    searchClients(columnSearch);
  };

  const onAcceptDelete = async () => {
    if (selectedClientDelete != null) {
      try {
        const deleteUrl =
          syncContacts === MANUALLY_SYNC
            ? `${window.API_V1}/cli/clients/${auth.cusNavId}/${selectedClientDelete.clientId}?syncClientlink=${deleteManuallySync}&login=${auth.mail}`
            : `${window.API_V1}/cli/clients/${auth.cusNavId}/${selectedClientDelete.clientId}?login=${auth.mail}`;
        const {
          data: { errors },
        } = await deleteClient({
          url: deleteUrl,
        });
        if (errors.length) {
          setOpenConfirmDelete(false);
          showAlert(ALERT_TYPE_ERROR, t('general_delete_error'));
          return;
        }
        setOpenConfirmDelete(false);
        showAlert(
          ALERT_TYPE_SUCCESS,
          t('client_delete_success').replace('{clientName}', selectedClientDelete.clientName)
        );
      } catch (ex) {
        setOpenConfirmDelete(false);
        showAlert(ALERT_TYPE_ERROR, t('general_delete_error'));
      }
    }
  };

  const onCancelDelete = () => {
    setSelectedClientDelete(undefined);
    setOpenConfirmDelete(false);
  };

  const onDelete = (client) => {
    if (syncContacts === MANUALLY_SYNC) {
      setSelectedClientDelete(client);
      setOpenManuallySync(true);
    } else {
      setSelectedClientDelete(client);
      setOpenConfirmDelete(true);
    }
  };

  const onCancelSyncContact = () => {
    setOpenConfirmDelete(true);
    setDeleteManuallySync(false);
    setOpenManuallySync(false);
  };

  const onOkSyncContact = () => {
    setOpenManuallySync(false);
    setOpenConfirmDelete(true);
    setDeleteManuallySync(true);
  };

  const handleTypeEventClick = (e, type, currentClient) => {
    const { localName } = e.target;
    if (type === 'edit' && (localName === 'div' || localName === 'td' || localName === 'p')) {
      history.push(`clients/update/${currentClient.clientId}`);
    }
    if (type === 'btnEdit') {
      history.push(`/clients/update/${currentClient.clientId}`);
    }
    if (type === 'delete') {
      onDelete(currentClient);
    }
    if (type === 'newCommunication') {
      history.push(`/clients/update/${currentClient.clientId}`);
      dispatch(setNewCommunication('fromClient'));
    }
  };

  return (
    <div className="control-panel__table table-clients">
      <TableComponent>
        <thead>
          <tr>
            <th>
              <TableHeader>
                <TableHeaderText>{t('clients_name_column')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterNameColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.clientName ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  onClick={() => null}
                />
                <FilterDropdownInput
                  dropdownOptions={getOptionsFromAllClient('clientName', 'clientName')}
                  target="filterNameColumn"
                  fieldSelectedOption="ClientName"
                  fieldSearchText="ClientName"
                  column="clientName"
                  onSearch={onSearchOptionOrText}
                />
              </TableHeader>
            </th>
            <th>
              <TableHeader>
                <TableHeaderText>{t('clients_number_id_column')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterIdColumn"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.cif ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  onClick={() => null}
                />
                <FilterMultiSelect
                  options={getOptionsFromAllClient('cif', 'cif')}
                  target="filterIdColumn"
                  fieldSearch="Cif"
                  column="cif"
                  onSearch={onSearchMultipleOptions}
                />
              </TableHeader>
            </th>
            <th>
              <TableHeader>
                <TableHeaderText>{t('general_phone')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterContactPhone"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.contactPhone ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  onClick={() => null}
                />
                <FilterDropdownInput
                  dropdownOptions={getOptionsFromAllClient('contactPhone', 'contactPhone')}
                  target="filterContactPhone"
                  fieldSelectedOption="ContactPhone"
                  fieldSearchText="ContactPhone"
                  column="contactPhone"
                  onSearch={onSearchOptionOrText}
                />
              </TableHeader>
            </th>
            <th>
              <TableHeader>
                <TableHeaderText>{t('clients_address_column')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterDirection"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.direction ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  onClick={() => null}
                />
                <FilterDropdownInput
                  dropdownOptions={getOptionsFromAllClient('direction', 'direction')}
                  target="filterDirection"
                  fieldSelectedOption="Direction"
                  fieldSearchText="Direction"
                  column="direction"
                  onSearch={onSearchOptionOrText}
                />
              </TableHeader>
            </th>
            <th>
              <TableHeader>
                <TableHeaderText>{t('clients_city_column')}</TableHeaderText>
                <ButtonIcon
                  title={t('general_button_filter')}
                  id="filterState"
                  className="button-icon"
                  Icon={() =>
                    columnSearch.state ? (
                      <FilterAppliedIcon fill={colors.black} />
                    ) : (
                      <FilterIcon fill={colors.black} />
                    )
                  }
                  onClick={() => null}
                />
                <FilterDropdownInput
                  dropdownOptions={getOptionsFromAllClient('local', 'local')}
                  target="filterState"
                  fieldSelectedOption="Local"
                  fieldSearchText="Local"
                  column="local"
                  onSearch={onSearchOptionOrText}
                />
              </TableHeader>
            </th>
            {tagsList[0] !== null && (
              <th>
                <TableHeader>
                  <TableHeaderText>{t('documents_search_classification')}</TableHeaderText>
                  <ButtonIcon
                    title={t('general_button_filter')}
                    id="filterTagsClient"
                    className="button-icon"
                    Icon={() =>
                      columnSearch.tagsClient ? (
                        <FilterAppliedIcon fill={colors.black} />
                      ) : (
                        <FilterIcon fill={colors.black} />
                      )
                    }
                    onClick={() => null}
                  />
                  <FilterDropdownInput
                    dropdownOptions={getTagsOptions('tagsClient', 'tagsClient')}
                    target="filterTagsClient"
                    fieldSelectedOption="tagsClient"
                    fieldSearchText="tagsClient"
                    column="tagsClient"
                    onSearch={onSearchOptionOrText}
                  />
                </TableHeader>
              </th>
            )}
            {clientStates[0] !== null && (
              <th>
                <TableHeader>
                  <TableHeaderText>{t('documents_search_status')}</TableHeaderText>
                  <ButtonIcon
                    title={t('general_button_filter')}
                    id="filterStateClient"
                    className="button-icon"
                    Icon={() =>
                      columnSearch.stateClient ? (
                        <FilterAppliedIcon fill={colors.black} />
                      ) : (
                        <FilterIcon fill={colors.black} />
                      )
                    }
                    onClick={() => null}
                  />
                  <FilterDropdownInput
                    dropdownOptions={getOptionsFromAllClient('stateClient', 'stateClient')}
                    target="filterStateClient"
                    fieldSelectedOption="stateClient"
                    fieldSearchText="stateClient"
                    column="stateClient"
                    onSearch={onSearchOptionOrText}
                  />
                </TableHeader>
              </th>
            )}
            <th>
              <TableHeader>
                <TableHeaderText>{t('general_column_action')}</TableHeaderText>
              </TableHeader>
            </th>
          </tr>
        </thead>
        <tbody>
          {currentClients.map((currentClient, index) => (
            <tr
              key={currentClient.clientId}
              className="action-row-client"
              onClick={(e) => handleTypeEventClick(e, 'edit', currentClient)}
            >
              <td>
                <TableBodyText aria-hidden="true">
                  <p id={`clientName-${index}`}>{currentClient.clientName}</p>
                  <UncontrolledTooltip placement="top" target={`clientName-${index}`}>
                    {currentClient.clientName}
                  </UncontrolledTooltip>
                </TableBodyText>
              </td>
              <td>
                <TableBodyText>
                  <p id={`cif-${index}`}>{currentClient.cif}</p>
                  <UncontrolledTooltip placement="top" target={`cif-${index}`}>
                    {currentClient.cif}
                  </UncontrolledTooltip>
                </TableBodyText>
              </td>
              <td>
                <TableBodyText>
                  <p id={`contactPhone-${index}`}>{currentClient.contactPhone}</p>
                  <UncontrolledTooltip flip target={`contactPhone-${index}`}>
                    {currentClient.contactPhone}
                  </UncontrolledTooltip>
                </TableBodyText>
              </td>
              <td>
                <TableBodyText>
                  <p id={`direction-${index}`}>{currentClient.direction}</p>
                  <UncontrolledTooltip placement="top" target={`direction-${index}`}>
                    {currentClient.direction}
                  </UncontrolledTooltip>
                </TableBodyText>
              </td>
              <td>
                <TableBodyText>
                  <p id={`local-${index}`}>{currentClient.local}</p>
                  <UncontrolledTooltip placement="top" target={`local-${index}`}>
                    {currentClient.local}
                  </UncontrolledTooltip>
                </TableBodyText>
              </td>
              {tagsList[0] !== null && (
                <td>
                  <TableBodyText>
                    <p id={`tagsClient-${index}`} className="">
                      <div className={classNames('classificationWrapper')}>
                        <DropDownButtonComponent
                          cssClass="e-caret-hide"
                          id="classification-button"
                          items={getTagsNames(currentClient.tagsClient)}
                        >
                          {getTagsCount(currentClient.tagsClient)}
                        </DropDownButtonComponent>
                      </div>
                    </p>
                  </TableBodyText>
                </td>
              )}
              {clientStates[0] !== null && (
                <td>
                  <TableBodyText>
                    <p
                      id={`stateClient-${index}`}
                      className="status-wrapper"
                      style={{
                        backgroundColor: getClientStateColor(currentClient.stateClient),
                        color: colors.black,
                      }}
                    >
                      {clientStates !== undefined
                        ? getClientStateName(currentClient.stateClient)
                        : ''}
                    </p>
                  </TableBodyText>
                </td>
              )}
              <td>
                <div className="d-flex">
                  <ButtonIcon
                    id={`edit-icon-client-${index}`}
                    title={t('clients_edit_client')}
                    type="button"
                    Icon={() => <EditIcon fill={colors.suvaGrey} />}
                    className="button-icon ml-n4"
                    onClick={(e) => handleTypeEventClick(e, 'btnEdit', currentClient)}
                  />
                  <ButtonIcon
                    id={`trash-icon-client-${index}`}
                    title={t('general_delete')}
                    type="button"
                    Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                    className="button-icon"
                    onClick={(e) => handleTypeEventClick(e, 'delete', currentClient)}
                  />
                  <ButtonIcon
                    id={`conversation-icon-client-${index}`}
                    title={t('notification_communication_new')}
                    type="button"
                    Icon={() => <ConversationIcon fill={colors.suvaGrey} />}
                    className="button-icon"
                    onClick={(e) => handleTypeEventClick(e, 'newCommunication', currentClient)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableComponent>
      {!currentClients.length && (
        <div className="d-flex flex-column justify-content-center align-items-center">
          <ClientIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
          <span>{t('client_not_found')}</span>
        </div>
      )}
      {clients.length > 0 && (
        <Pagination
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={clients.length}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onGoToPage={onGoToPage}
          titlePagination={t('home_client_section_label')}
        />
      )}
      <ConfirmModal
        isOpen={openConfirmDelete}
        toggle={onCancelDelete}
        confirmMessage={t('client_delete_confirmation').replace(
          '{clientName}',
          selectedClientDelete?.clientName
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelDelete}
        onOk={onAcceptDelete}
      />
      <ConfirmModal
        isOpen={openManuallySync}
        toggle={onCancelSyncContact}
        confirmMessage={t('client_management_contact_synchronize_data')}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelSyncContact}
        onOk={onOkSyncContact}
      />
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={closeAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={closeAlert}
      />
      {(loading || loadingDelete) && <MainSpinner />}
    </div>
  );
};

export default Table;
